import { useMemo } from 'react';
import { AuthMode } from '@hasura/auth-ui';

import { AUTH_METHODS } from '../../utils/constants';

type AuthModesFromEnv = AuthMode | 'saml' | 'oidc';

function processAuthMethods(): AuthModesFromEnv[] {
  // since AUTH_METHODS is potentially any value, we have to do some checks:
  if (typeof AUTH_METHODS !== 'string' || !AUTH_METHODS) {
    return [] as AuthModesFromEnv[];
  }

  return AUTH_METHODS.split(',')
    .map(mode => mode.trim())
    .filter(Boolean) as AuthModesFromEnv[];
}

export const useAuthModes = () => {
  const authModesFromEnv = useMemo(() => processAuthMethods(), []);

  // convert to AuthMode type used by @hasura/auth-ui
  // this is what determines which forms are shown in the UI
  // 'saml' and 'oidc' are treated as 'sso' and the backend will handle them accordingly
  const modes = useMemo(() => {
    const modeSet = authModesFromEnv.reduce<Set<AuthMode>>((acc, mode) => {
      if (mode === 'saml' || mode === 'oidc') {
        acc.add('sso');
      } else {
        acc.add(mode);
      }
      return acc;
    }, new Set<AuthMode>());

    return Array.from(modeSet);
  }, [authModesFromEnv]);

  const isSocialSignInSupported = useMemo(
    () =>
      authModesFromEnv.includes('google') ||
      authModesFromEnv.includes('github'),
    [authModesFromEnv]
  );

  const isEmailSignInSupported = useMemo(
    () => authModesFromEnv.includes('password'),
    [authModesFromEnv]
  );

  const isSSOAuthSupported = useMemo(
    () =>
      authModesFromEnv.includes('saml') || authModesFromEnv.includes('oidc'),
    [authModesFromEnv]
  );

  return {
    modes,
    isEmailSignInSupported,
    isSocialSignInSupported,
    isSSOAuthSupported,
  };
};
